import type { CompetencySlug, QuestionType } from '@evaltick/shared';
import { BaseAPIClient } from './base';

// =============================================================================
// TYPES
// =============================================================================

/** Summary of the QuestionSet a package references — not the full item list. */
export interface AssessmentPackageQuestionSet {
  id: string;
  name: string;
  /** Null means this question set has no HTS questionnaire attached. */
  htsQuestionnaireId: string | null;
  _count: { items: number };
}

export interface AssessmentPackage {
  id: string;
  name: string;
  description: string | null;
  unlockPriceCents: number;
  unlockIncludedCredits: number;
  interviewCostCredits: number;
  questionSetId: string;
  questionSet: AssessmentPackageQuestionSet;
  coachingEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AssessmentPackagePayload {
  name: string;
  description?: string;
  unlockPriceCents: number;
  unlockIncludedCredits: number;
  interviewCostCredits: number;
  questionSetId: string;
  coachingEnabled: boolean;
}

export type UpdateAssessmentPackagePayload = Partial<AssessmentPackagePayload>;

// re-exported so consumers don't need a separate import for the enums used in items
export type { CompetencySlug, QuestionType };

// =============================================================================
// SERVICE
// =============================================================================

export class AdminAssessmentPackagesService extends BaseAPIClient {
  /** Admin: list all packages. */
  async list(): Promise<AssessmentPackage[]> {
    return this.get<AssessmentPackage[]>('/admin/packages');
  }

  /** Admin: get a single package. */
  async getById(id: string): Promise<AssessmentPackage> {
    return this.get<AssessmentPackage>(`/admin/packages/${id}`);
  }

  /** Admin: create a package. */
  async create(payload: AssessmentPackagePayload): Promise<AssessmentPackage> {
    return this.post<AssessmentPackage>('/admin/packages', payload);
  }

  /** Admin: update a package. */
  async update(id: string, payload: UpdateAssessmentPackagePayload): Promise<AssessmentPackage> {
    return this.patch<AssessmentPackage>(`/admin/packages/${id}`, payload);
  }

  /** Admin: delete a package. */
  async remove(id: string): Promise<void> {
    return this.delete(`/admin/packages/${id}`);
  }
}

export default AdminAssessmentPackagesService;
